import { useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';

// Session timeout in milliseconds (15 minutes)
const SESSION_TIMEOUT = 15 * 60 * 1000;
// Warning before timeout (1 minute before)
const WARNING_BEFORE_TIMEOUT = 1 * 60 * 1000;

export function useSessionTimeout(onTimeout) {
  const timeoutRef = useRef(null);
  const warningRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  const resetTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    
    // Clear existing timers
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (warningRef.current) {
      clearTimeout(warningRef.current);
    }

    // Set warning timer
    warningRef.current = setTimeout(() => {
      toast.warning('Your session will expire in 1 minute due to inactivity', {
        duration: 10000,
        id: 'session-warning',
      });
    }, SESSION_TIMEOUT - WARNING_BEFORE_TIMEOUT);

    // Set timeout timer
    timeoutRef.current = setTimeout(() => {
      toast.error('Session expired due to inactivity', {
        duration: 5000,
        id: 'session-expired',
      });
      onTimeout();
    }, SESSION_TIMEOUT);
  }, [onTimeout]);

  useEffect(() => {
    // Events that reset the timer
    const events = [
      'mousedown',
      'mousemove',
      'keydown',
      'scroll',
      'touchstart',
      'click',
    ];

    // Throttle the reset to avoid too many resets
    let throttleTimer = null;
    const throttledReset = () => {
      if (!throttleTimer) {
        throttleTimer = setTimeout(() => {
          resetTimer();
          throttleTimer = null;
        }, 1000); // Only reset once per second max
      }
    };

    // Add event listeners
    events.forEach((event) => {
      document.addEventListener(event, throttledReset, { passive: true });
    });

    // Initialize timer
    resetTimer();

    // Cleanup
    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, throttledReset);
      });
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (warningRef.current) {
        clearTimeout(warningRef.current);
      }
      if (throttleTimer) {
        clearTimeout(throttleTimer);
      }
    };
  }, [resetTimer]);

  return { resetTimer };
}

export default useSessionTimeout;
